/**
 * "Come back to where I was" for the resume flow.
 *
 * Finishing your Portal Page takes four steps, several saves and sometimes a reload, so the
 * origin travels as a ?next= param rather than living in component state — it survives all
 * three, and it's visible in the URL when something goes wrong.
 *
 * Everything read back out is validated: only a same-origin path is ever
 * returned. A ?next=https://evil.example or a protocol-relative //evil.example
 * would otherwise turn our own CTA into an open redirect.
 */

/** A path is safe if it's rooted, single-slashed, and carries no scheme. */
export function safeReturnTo(raw, fallback = null) {
  if (!raw || typeof raw !== 'string') return fallback;
  let value = raw.trim();
  try { value = decodeURIComponent(value); } catch { /* keep the raw form */ }
  if (!value.startsWith('/')) return fallback;
  // "//host" and "/\host" are both read as protocol-relative by browsers.
  if (value.startsWith('//') || value.startsWith('/\\')) return fallback;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return fallback;
  return value;
}

/** Attach a return path to a destination, keeping any query it already has. */
export function withReturnTo(href, returnPath) {
  const target = safeReturnTo(returnPath);
  if (!href || !target) return href;
  // Don't send someone back to the page they're about to be on.
  if (target.split('?')[0] === href.split('?')[0]) return href;
  return `${href}${href.includes('?') ? '&' : '?'}next=${encodeURIComponent(target)}`;
}

/** The current location as a path we can return to later. */
export function currentPath(location) {
  if (!location) return null;
  return `${location.pathname}${location.search || ''}`;
}

/**
 * Query params that name a record belonging to ONE account. They're stripped
 * when you become a different user: an id scoped to the account you just left
 * resolves to "not found" for the new one, which reads as a broken app rather
 * than a switched account.
 */
const ACCOUNT_SCOPED_PARAMS = ['thread'];

/**
 * Where to land after switching (or adding) an account: exactly where you were.
 *
 * Reloading to "/" every time bounced you to Featured no matter what you were
 * doing — switch accounts while reading Messages and you lost your place. The
 * reload itself stays: it's what re-initialises the whole app cleanly for the
 * new user.
 */
export function samePlaceAfterAccountChange(loc = (typeof window !== 'undefined' ? window.location : null)) {
  if (!loc) return '/';
  const here = safeReturnTo(`${loc.pathname || '/'}${loc.search || ''}`, '/');
  const [path, qs] = here.split('?');
  if (!qs) return path || '/';
  const params = new URLSearchParams(qs);
  ACCOUNT_SCOPED_PARAMS.forEach((k) => params.delete(k));
  const rest = params.toString();
  return rest ? `${path}?${rest}` : (path || '/');
}

/**
 * Sign-up v3: where a brand-new account goes after any sign-in — `/onboard`, carrying the
 * destination it was headed for as `?next=` (none for the default landing).
 */
export function onboardingHref(redirect) {
  const next = redirect && redirect !== '/featured' && redirect !== '/square' ? redirect : '';
  return next ? `/onboard?next=${encodeURIComponent(next)}` : '/onboard';
}
